import { createMultiStyleConfigHelpers, defineStyle } from '@chakra-ui/react';

export const StepsParts = [
  'container',
  'steps',
  'step.inactive',
  'step.active',
  'step.completed',
  'step.invalid',
] as const;

export const StepsComponentName = 'Steps' as const;

const { defineMultiStyleConfig, definePartsStyle } = createMultiStyleConfigHelpers(StepsParts);

const baseStyleContainer = defineStyle({
  bgColor: 'gray.100',
  paddingY: '6',
  paddingX: '2',
  marginX: 'auto',
  width: '100%',
});

const baseStyleSteps = defineStyle({
  display: 'flex',
  gap: 0,
  justifyContent: 'center',
  margin: 0,
  padding: 0,
  position: 'relative',
});

const connectorLine = {
  content: '""',
  position: 'absolute',
  top: '20px',
  left: '0',
  width: '60%',
  height: '1px',
  zIndex: 0,
  transform: 'translateX(-50%)',
};

const baseStyleStep = defineStyle({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flex: 1,
  minWidth: '64px',
  margin: 0,
  listStyle: 'none',
  cursor: 'pointer',

  '&:not(:first-of-type)::before': {
    ...connectorLine,
    bgColor: 'gray.300',
  },

  '& .step-circle': {
    borderRadius: '50%',
    background: 'white',
    border: '2px solid',
    borderColor: 'gray.300',
    color: 'gray.500',
    fontSize: 'sm',
    fontWeight: 'bold',
    width: '40px',
    height: '40px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    zIndex: 1,
  },

  '& .step-label': {
    display: { base: 'none', md: 'block' },
    marginTop: '2',
    fontSize: 'sm',
    color: 'gray.700',
    textAlign: 'center',
  },
});

const baseStyleStepInactive = defineStyle({
  ...baseStyleStep,
});

const baseStyleStepActive = defineStyle({
  ...baseStyleStep,
  '&:not(:first-of-type)::before': {
    ...baseStyleStep['&:not(:first-of-type)::before'],
    bgColor: 'green.500',
  },
  '& .step-circle': {
    ...baseStyleStep['& .step-circle'],
    background: 'orange.500',
    borderColor: 'orange.500',
    color: 'white',
  },
  '& .step-label': {
    ...baseStyleStep['& .step-label'],
    fontWeight: 'bold',
    color: 'gray.900',
  },
});

const baseStyleStepCompleted = defineStyle({
  ...baseStyleStep,
  '&:not(:first-of-type)::before': {
    ...baseStyleStep['&:not(:first-of-type)::before'],
    bgColor: 'green.500',
  },
  '& .step-circle': {
    ...baseStyleStep['& .step-circle'],
    background: 'green.500',
    borderColor: 'green.500',
    color: 'white',
  },
});

const baseStyleStepInvalid = defineStyle({
  ...baseStyleStep,
  '&:not(:first-of-type)::before': {
    ...baseStyleStep['&:not(:first-of-type)::before'],
    bgColor: 'red.300',
  },
  '& .step-circle': {
    ...baseStyleStep['& .step-circle'],
    background: 'red.500',
    borderColor: 'red.500',
    color: 'white',
  },
});

const baseStyle = definePartsStyle({
  container: baseStyleContainer,
  steps: baseStyleSteps,
  'step.inactive': baseStyleStepInactive,
  'step.active': baseStyleStepActive,
  'step.completed': baseStyleStepCompleted,
  'step.invalid': baseStyleStepInvalid,
});

export const Steps = defineMultiStyleConfig({
  baseStyle,
});
